import { IComplex, IDevelopmentSpecification, IStatus } from 'src/typings/complexes';
import { IDogeFields, IDogeFieldsOptional } from 'src/typings/global-config';
import { IAction } from 'src/typings';
import { IPlacement } from 'src/typings/placement';
import { IUsers } from 'src/typings/user';
import { ITranslate } from 'src/typings/viewConfig';
import {
  IAccessRulesBlock,
  IPropertyRule,
  IWriteBlocker,
  TWriteBlockedAction,
} from 'src/accessControl';
import { ActionTypes } from './actions';
import { IResponsibleUser } from '../../typings/filter';

export interface IHouse extends IDogeFields {
  id: number;
  numeration: {
    [key: string]: string;
  };
  complex: IComplex;
  developmentSpecification?: IDevelopmentSpecification
}

export interface ICreatioDeal extends IDogeFields {
  creatioProject: string;
  creatioStage: string;
  id: string;
  externalId: string;
}

export interface INetHuntDeal extends IDogeFields {
  responsibleUser: IResponsibleUser;
  stage: string;
  funnel: string;
  externalName: string;
  id: string;
  externalId: string;
  netHuntLink: string;
  placements: string[];
}
export interface IBitrixDealCategory extends IDogeFieldsOptional {
  'bitrixExternalCategoryName'?: string;
  'bitrixExternalCategoryId'?: string;
}

export interface IBitrixDealClient extends IDogeFieldsOptional {
  'name'?: string,
  'phones'?: string[],
  'bitrixProject'?: string,
  'bitrixExternalClientId'?: number
}

export interface IBitrixDealStage extends IDogeFieldsOptional {
    'bitrixExternalStageName'?: string,
    'bitrixExternalStageId'?: string,
}

export interface IBitrixDealResponsibleUser extends IDogeFieldsOptional {
    'email'?: string,
    'username'?: string,
    'icon'?: string
}

export interface IBitrixDealPlacementStatus extends IDogeFields {
  color: string;
  crosstableColor: string;
  crosstableName: ITranslate | null;
  moduleName: ITranslate | null;
  amoCanBeSetForDeal: boolean;
}

export interface IBitrixDealPlacementHouse extends IDogeFields {
  numeration: ITranslate | null;
}

export interface IBitrixDealPlacementSection extends IDogeFields {
  numeration: ITranslate | null;
}

export interface IBitrixDealPlacementType extends IDogeFields {
  title: ITranslate | null;
}

export interface IBitrixDealPlacementComplex extends IDogeFields {
  id: string;
}
export interface IBitrixDealPlacementFloors extends IDogeFields {
  numeration: ITranslate | null;
}

export type IBitrixAccessRules = {
  '@accessRules'?: IAccessRulesBlock
};

export type IBitrixDealsInDealPlacement =
  Omit<IBitrixDeal, 'placement'> & {
  placement: string;
};

export interface IBitrixDealPlacement extends IDogeFieldsOptional, IBitrixAccessRules {
  id: string;
  placementStatus?: IBitrixDealPlacementStatus,
  house?: IBitrixDealPlacementHouse,
  section?: IBitrixDealPlacementSection,
  numeration?: ITranslate | null;
  placementType?: IBitrixDealPlacementType,
  complex?: IBitrixDealPlacementComplex,
  floors?: IBitrixDealPlacementFloors[],
  bitrixDeals?: IBitrixDealsInDealPlacement[],
  creatioOpportunities?: [],
  netHuntOpportunities?: [],
  virtual?: IBitrixDealVirtual,
}

export interface IBitrixDeal extends IDogeFieldsOptional, IBitrixAccessRules {
  id: string;
  bitrixCategory?: IBitrixDealCategory;
  bitrixDealClient?: IBitrixDealClient | null;
  bitrixExternalDealId?: string;
  bitrixProject?: string;
  bitrixStage?: IBitrixDealStage;
  bitrixExternalName?: string;
  bitrixExternalUrl?: string;
  placement?: IBitrixDealPlacement | null;
  responsibleUser?: IBitrixDealResponsibleUser;
  virtual?: IBitrixDealVirtual;
}
// Canonical access-rules contract lives in src/accessControl (matches the backend
// AccessControl serializer: readBlockReason + ui, not the legacy writeBlockReason).
// The aliases below keep the historical names used across the widget components.
export type IBitrixDealAccessRulesPropertiesWrite = TWriteBlockedAction;
export type IBitrixDealAccessRulesWrite = IWriteBlocker;
export type IBitrixDealAccessRulesProperties = IPropertyRule;
export type IBitrixDealAccessRules = IAccessRulesBlock;

export interface IBitrixDealVirtual {
  lastPlacementLink?: {
    by?: IUsers | null;
    at?: string;
  } | null;
}

export interface IBitrixDealVirtualStatusHistory {
  statusHistory: []
}

export interface IHouses {
  complexId: number|string;
  data: IHouse[];
}

export interface ICrosstable {
  complex: IComplex | null;
  statuses: IStatus[] | null;
  houses: IHouses | null;
  availableComplexes: IComplex[] | null | undefined;
  bitrixDeal: IBitrixDeal | null;
  creatioDeal: ICreatioDeal | null;
  netHuntDeal: INetHuntDeal | null;
  defaultPlacementType: string | null;
}

export const initialState: ICrosstable = {
  complex: null,
  statuses: null,
  houses: null,
  availableComplexes: undefined,
  bitrixDeal: null,
  creatioDeal: null,
  netHuntDeal: null,
  defaultPlacementType: null,
};

export const crosstableReducer = (
  state = initialState,
  { type, payload }: IAction,
): ICrosstable => {
  switch (type) {
    case ActionTypes.SET_COMPLEX:
      return {
        ...state,
        complex: payload,
      };
    case ActionTypes.SET_STATUSES:
      return {
        ...state,
        statuses: payload,
      };
    case ActionTypes.SET_HOUSES:
      return {
        ...state,
        houses: payload,
      };
    case ActionTypes.SET_AVAILABLE_COMPLEXES:
      return {
        ...state,
        availableComplexes: payload,
      };
    case ActionTypes.SET_BITRIX_DEAL:
      return {
        ...state,
        bitrixDeal: payload,
      };
    case ActionTypes.SET_CREATIO_DEAL:
      return {
        ...state,
        creatioDeal: payload,
      };
    case ActionTypes.SET_NETHUNT_DEAL:
      return {
        ...state,
        netHuntDeal: payload,
      };
    case ActionTypes.SET_DEFAULT_PLACEMENT_TYPE:
      return {
        ...state,
        defaultPlacementType: payload,
      };

    default:
      return state;
  }
};
